import { useFragment } from '@apollo/client';
import type { GridColDef, GridRowId } from '@mui/x-data-grid-pro';
import { useMemo } from 'react';
import {
  DataItemCellSelect__DataItemListed as DataItemListed,
  type DataItemCellSelect__DataSource as DataSource,
} from '../DataItemCellSelect.generated';

export interface DataItemData {
  [field: string]: string;
}

export interface SelectedCellParams {
  id: GridRowId;
  field: string;
  value?: string;
}

// A device-aware binding names a column only; the row is chosen at playback.
export interface SelectedColumnParams {
  dataSourceId: number;
  field: string;
}

export const getLabel = (item: DataItemListed) => {
  const {
    data,
    source: { foreignIdName, keys },
  } = item;

  const dataItem = data as Record<string, unknown>;

  const firstHeader = keys.filter((x) => x !== foreignIdName).at(0);
  if (!firstHeader) return Object.values(dataItem).at(0);

  return dataItem[firstHeader];
};

export const useCachedDataItem = (id: number | undefined) => {
  const { complete, data } = useFragment({
    fragment: DataItemListed,
    from: { __typename: 'DataItem', id },
  });

  // there are times data is a blank object
  if (!complete || !data.id) return null;

  return data;
};

const getCellClasses = (
  id: GridRowId,
  field: string,
  value: string,
  selectedCell: SelectedCellParams | undefined,
  selectedColumn: string | undefined,
  keyColumn: string | undefined,
) => {
  const classes = [];

  if (keyColumn === field) classes.push('key');

  if (selectedColumn === field) classes.push('selected');

  if (selectedCell?.id === String(id) && selectedCell.field === field) classes.push('selected');

  if (!value) classes.push('empty');

  return classes.join(' ');
};

export const useDataSourceTable = (
  dataSource: DataSource | undefined,
  selectedCell: SelectedCellParams | undefined,
  selectedColumn?: string,
  // In device-aware mode the key column is what the match runs on, so it is not a value to bind.
  keyColumn?: string,
) => {
  const columns: GridColDef[] = useMemo(() => {
    if (!dataSource) return [];
    return dataSource.keys.map((x) => ({
      field: x,
      flex: 1,
      headerClassName: [keyColumn === x && 'key', selectedColumn === x && 'selected']
        .filter(Boolean)
        .join(' '),
      headerName: x,
      minWidth: 100,
      sortable: false,
      renderCell: ({ field, id, value }) => (
        <div
          className={getCellClasses(
            id,
            field,
            String(value),
            selectedCell,
            selectedColumn,
            keyColumn,
          )}
        >
          {value}
        </div>
      ),
    }));
  }, [dataSource, keyColumn, selectedCell, selectedColumn]);

  const rows = useMemo(
    () => dataSource?.items.nodes.map((x) => ({ ...x.data, id: x.id })) ?? [],
    [dataSource?.items.nodes],
  );

  return {
    columns,
    rows,
  };
};
